import { useFormik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import Form from 'react-bootstrap/Form';
import InputGroup from "react-bootstrap/InputGroup";
import Button from "react-bootstrap/Button";
import Stack from "react-bootstrap/esm/Stack";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faFilm, faPlus, faCheck, faBookmark, faThumbTack } from "@fortawesome/free-solid-svg-icons";

import {
    CategoryWithCountsViewModel,
    CreateBookmarkBindingModel,
    CreateBookmarksBindingModel,
    CreateVideoBindingModel,
    CreateVideosBindingModel,
    ResolveVideoBindingModel,
    UpdateVideoBindingModel,
    VideoLibraryApiClient,
    VideoViewModel
} from "../../apiClient";
import { getErrorMessage } from "../../helpers/errorHelper";
import { CategorySelect, CountType } from "../inputs/categorySelect";

export const getAddVideoBookmarkletHref = (origin: string = typeof window !== 'undefined' ? window.location.origin : '') =>
    `javascript:(function(){window.open('${origin}/videos/add?url='+encodeURIComponent(window.location.href), '_blank');})();`;

type FormValues = {
    title: string;
    categoryName: string;
    url: string;
}

type VideoFormProps = {
    formik: ReturnType<typeof useFormik<FormValues>>;
    isEdit: boolean;
    resolvingVideoTitle: boolean;
    categories: CategoryWithCountsViewModel[];
    handleUrlBlur: () => void;
    error?: string | null;
    showAddBookmarkButton: boolean;
    addAsBookmark: () => void;
    setUrlChanged?: (changed: boolean) => void;
};

export const VideoForm = ({
                              formik,
                              isEdit,
                              resolvingVideoTitle,
                              categories,
                              handleUrlBlur,
                              error,
                              showAddBookmarkButton,
                              addAsBookmark,
                              setUrlChanged
                          }: VideoFormProps) => (
    <Form onSubmit={formik.handleSubmit}>
        <Stack gap={3}>
            {error && (
                <Alert variant="danger" className="shadow-sm border-start border-4 border-danger">
                    <div style={{ whiteSpace: 'pre-wrap' }}>{error}</div>
                </Alert>
            )}

            <fieldset disabled={formik.isSubmitting || resolvingVideoTitle}>
                <Stack gap={3}>
                    <Form.Group controlId="video-url">
                        <Form.Label>Url</Form.Label>
                        <InputGroup>
                            <InputGroup.Text className="bg-body text-secondary">
                                <FontAwesomeIcon icon={faLink} size="sm" />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                required
                                name="url"
                                placeholder="https://www.youtube.com/watch?v=..."
                                value={formik.values.url}
                                onChange={(e) => {
                                    formik.handleChange(e);
                                    setUrlChanged && setUrlChanged(true);
                                }}
                                onBlur={handleUrlBlur}
                                disabled={isEdit}
                            />
                        </InputGroup>
                    </Form.Group>

                    <Form.Group controlId="video-title">
                        <Form.Label className="d-flex align-items-center justify-content-between">
                            <span>Title</span>
                            {resolvingVideoTitle && (
                                <span className="small text-secondary d-inline-flex align-items-center gap-1">
                                    <Spinner animation="border" size="sm" />
                                    <span>Fetching video title...</span>
                                </span>
                            )}
                        </Form.Label>
                        <InputGroup>
                            <InputGroup.Text className="bg-body text-secondary">
                                <FontAwesomeIcon icon={faFilm} size="sm" />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                required
                                name="title"
                                placeholder="Video title"
                                value={formik.values.title}
                                onChange={formik.handleChange}
                            />
                        </InputGroup>
                    </Form.Group>

                    <Form.Group>
                        <CategorySelect
                            categories={categories}
                            value={formik.values.categoryName}
                            name="categoryName"
                            onChange={value => formik.setFieldValue('categoryName', value)}
                            countType={CountType.Videos}
                        />
                    </Form.Group>
                </Stack>
            </fieldset>

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 pt-2">
                <Stack direction="horizontal" gap={2}>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={!formik.isValid || formik.isSubmitting || resolvingVideoTitle}
                    >
                        {formik.isSubmitting ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={isEdit ? faCheck : faPlus} className="me-2" />
                                {isEdit ? 'Edit' : 'Add'} video
                            </>
                        )}
                    </Button>
                    {showAddBookmarkButton && (
                        <Button
                            variant="secondary"
                            onClick={addAsBookmark}
                            disabled={formik.isSubmitting || resolvingVideoTitle}
                        >
                            <FontAwesomeIcon icon={faBookmark} className="me-2" />
                            Add as Bookmark
                        </Button>
                    )}
                </Stack>

                {!isEdit && (
                    <div className="d-flex align-items-center gap-2 small text-body-secondary">
                        <span>Drag to bookmarks bar:</span>
                        <a
                            ref={(el) => {
                                if (el) el.setAttribute('href', getAddVideoBookmarkletHref());
                            }}
                            href="#bookmarklet"
                            onClick={(e) => e.preventDefault()}
                            className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                            title="Drag this link to your browser's bookmarks bar to quickly add any video page"
                        >
                            <FontAwesomeIcon icon={faThumbTack} size="xs" />
                            <span>+ Add Video</span>
                        </a>
                    </div>
                )}
            </div>
        </Stack>
    </Form>
);

type Props = {
    apiClient: VideoLibraryApiClient;
    video?: VideoViewModel;
    initialUrl?: string;
    categories: CategoryWithCountsViewModel[];
    onSave: () => void;
}

export const VideoAddEditForm = ({apiClient, video, initialUrl, categories, onSave}: Props) => {
    const [resolvingVideoTitle, setResolvingVideoTitle] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [titleResolutionFailed, setTitleResolutionFailed] = useState<boolean>(false);
    const [urlChanged, setUrlChanged] = useState<boolean>(false);
    const resolvedInitialUrlRef = useRef<string | null>(null);
    const navigate = useNavigate();
    const isEdit = video?.id !== undefined;
    const formik = useFormik<FormValues>({
        initialValues: {
            title: video?.title ?? '',
            categoryName: video?.categoryName ?? '',
            url: video?.url ?? initialUrl ?? '',
        },
        onSubmit: async (values, helpers) => {
            setError(null);
            try {
                if (isEdit) {
                    await apiClient?.videosPUT(video.id, new UpdateVideoBindingModel({
                        title: values.title,
                        categoryName: values.categoryName !== '' ? values.categoryName : null,
                    }));
                } else {
                    const result = await apiClient?.videosPOST(new CreateVideosBindingModel({
                        videos: [
                            new CreateVideoBindingModel({
                                url: values.url,
                                title: values.title,
                                categoryName: values.categoryName !== '' ? values.categoryName : null,
                            })
                        ]
                    }));
                    const failedVideo = result?.failedVideos?.[0];
                    if (failedVideo) {
                        setError(failedVideo.reason ?? "Failed to add video.");
                        helpers.setSubmitting(false);
                        return;
                    }
                }
                onSave();
            } catch (e: any) {
                console.error(e);
                setError(getErrorMessage(e));
            } finally {
                helpers.setSubmitting(false);
            }
        }
    });

    const { setFieldValue } = formik;

    const resolveUrlTitle = useCallback(async (targetUrl: string) => {
        if (!targetUrl || !apiClient?.videoResolver) return;

        setResolvingVideoTitle(true);
        setUrlChanged(false);
        setTitleResolutionFailed(false);
        try {
            const resolved = await apiClient.videoResolver(new ResolveVideoBindingModel({
                url: targetUrl,
            }));
            if (resolved?.title) {
                await setFieldValue('title', resolved.title);
            }
            setTitleResolutionFailed(false);
        } catch (e) {
            console.error(e);
            setTitleResolutionFailed(true);
        } finally {
            setResolvingVideoTitle(false);
        }
    }, [apiClient, setFieldValue]);

    useEffect(() => {
        if (!isEdit && initialUrl && resolvedInitialUrlRef.current !== initialUrl) {
            resolvedInitialUrlRef.current = initialUrl;
            resolveUrlTitle(initialUrl);
        }
    }, [initialUrl, isEdit, resolveUrlTitle]);

    const handleUrlBlur = async () => {
        if (!formik.values.url) return;
        await resolveUrlTitle(formik.values.url);
    };

    const addAsBookmark = async () => {
        if (!formik.values.url) return;

        try {
            const result = await apiClient?.bookmarksPOST(new CreateBookmarksBindingModel({
                bookmarks: [
                    new CreateBookmarkBindingModel({
                        url: formik.values.url,
                        title: formik.values.title,
                        categoryName: formik.values.categoryName !== '' ? formik.values.categoryName : null,
                    }),
                ],
            }));
            const failedBookmark = result?.failedBookmarks?.[0];
            if (failedBookmark) {
                setError(failedBookmark.reason ?? "Failed to add bookmark.");
            } else {
                navigate('/bookmarks');
            }
        } catch (e) {
            console.error(e);
            setError(getErrorMessage(e));
        }
    };

    return (
        <VideoForm
            formik={formik}
            isEdit={isEdit}
            resolvingVideoTitle={resolvingVideoTitle}
            categories={categories}
            handleUrlBlur={handleUrlBlur}
            error={error}
            showAddBookmarkButton={!isEdit && titleResolutionFailed && !urlChanged}
            addAsBookmark={addAsBookmark}
            setUrlChanged={setUrlChanged}
        />
    );
};